import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/useI18n';
import { projectApi } from '../api/client';
import { getAvatarById, AVATAR_LIST } from '../utils/constants';
import Paywall from '../components/paywall';
import API_CONFIG from '../config/apiConfig';
import { Purchases } from '@revenuecat/purchases-capacitor';

export default function Profile() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, updateProfile, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [nickname, setNickname] = useState('');
  const [originalNickname, setOriginalNickname] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (user) {
      setNickname(user.nickname);
      setOriginalNickname(user.nickname);
      loadProjects();
    }
  }, [user]);

  async function loadProjects() {
    try {
      const res = await projectApi.getList();
      if (res.success) {
        setProjects(res.data.projects);
      }
    } catch (err) {
      console.error(t('loading') + ' ' + t('error'), err);
    }
  }

  function enableEdit() {
    setOriginalNickname(nickname);
    setEditMode(true);
  }

  function cancelEdit() {
    setNickname(originalNickname);
    setEditMode(false);
  }

  async function saveNickname() {
    const newName = nickname.trim();
    if (newName === originalNickname) {
      setEditMode(false);
      return;
    }

    try {
      // 调用用户 API 更新昵称
      const res = await fetch(`${API_CONFIG.FULL_API_BASE_URL}/user/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ nickname: newName }),
      });
      const data = await res.json();
      if (data.success) {
        updateProfile({ nickname: newName });
        setEditMode(false);
      } else {
        alert(data.error || t('updateFailed'));
      }
    } catch (e) {
      alert(t('requestFailed'));
    }
  }

  async function updateAvatar(avatarId) {
    try {
      const res = await fetch(`${API_CONFIG.FULL_API_BASE_URL}/user/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ avatar_id: avatarId }),
      });
      const data = await res.json();
      if (data.success) {
        updateProfile({ avatar_id: avatarId });
        setShowAvatarModal(false);
      }
    } catch (e) {
      alert(t('requestFailed' + e));
    }
  }

  async function upgradeToVip() {
    setIsProcessing(true);
    try {
      
      // 1. 获取产品列表
      const offeringsData = await Purchases.getOfferings();
      
      if (!offeringsData.current || !offeringsData.current.availablePackages.length) {
        alert(t('noProductsAvailable') || '暂无可用产品');
        return;
      }

      // 2. 显示产品选择弹窗（这里简化处理，取第一个产品）
      const pkg = offeringsData.current.availablePackages[0];
      
      // 3. 发起购买 - 使用正确的参数名 aPackage
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });

      // 4. 检查购买后的权益 - 使用正确的 entitlement 名称
      const entitlementId = 'lezhiyi-tech Pro'; // 需要与 RevenueCat 后台配置一致
      if (customerInfo.entitlements.active[entitlementId]) {
        // 5. 购买成功后，调用后端验证并更新 VIP 状态
        const response = await fetch(`${API_CONFIG.FULL_API_BASE_URL}/user/vip.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            product_id: pkg.product.identifier,
          }),
        });
        const data = await response.json();

        if (data.success) {
          updateProfile({ is_vip: 1 });
          alert(data.message || t('upgradeSuccess'));
        } else {
          alert(data.message || t('upgradeFailed'));
        }
      }
    } catch (error) {
      // 用户取消购买不显示错误
      if (error.userCancelled) {
        console.log('用户取消了购买');
        return;
      }
      console.error(t('upgradeToVip') + ' ' + t('error') + ':', error);
      alert(t('purchaseError') + ': ' + (error.message || ''));
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleLogout() {
    if (!confirm(t('confirmLogout'))) return;
    try {
      const res = await fetch(`${API_CONFIG.FULL_API_BASE_URL}/auth/logout.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        // 清除本地用户状态
        logout();
        // 使用 HashRouter 导航到登录页面
        navigate('/login', { replace: true });
      }
    } catch (e) {
      alert(t('networkErrorLogout'));
    }
  }

  const avatar = getAvatarById(user?.avatar_id);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-indigo-600 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex items-center">
          <button
            onClick={() => navigate('/')}
            className="mr-3 p-1 hover:bg-indigo-500 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <h1 className="text-lg font-bold">{t('profile')}</h1>
        </div>
      </nav>

      <main className="container mx-auto p-4 max-w-md space-y-6">
        {/* 用户信息卡片 */}
        <section className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center space-x-4">
            <div
              onClick={() => setShowAvatarModal(true)}
              className="relative shrink-0 group cursor-pointer"
            >
              <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center text-3xl border-2 border-white shadow-sm transition-transform group-hover:scale-105">
                {avatar}
              </div>
              <div className="absolute bottom-0 right-0 bg-white shadow-md rounded-full w-5 h-5 flex items-center justify-center text-[10px] border border-gray-100">
                ✏️
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-gray-400 font-mono truncate tracking-tight uppercase">
                {user?.email}
              </p>
              <div className="flex items-center mt-1">
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className={`text-lg font-bold text-gray-800 border-b-2 outline-none flex-1 min-w-0 bg-transparent transition-all truncate ${
                    editMode ? 'border-indigo-500 bg-white px-2' : 'border-transparent'
                  }`}
                  readOnly={!editMode}
                />

                <div className="flex items-center ml-2 shrink-0">
                  {!editMode ? (
                    <button
                      onClick={enableEdit}
                      className="btn-emoji whitespace-nowrap text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg hover:bg-indigo-100"
                    >
                      ✏️ <span className="ml-1">{t('editNickname')}</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={saveNickname}
                        className="btn-emoji text-xl w-9 h-9 bg-green-50 rounded-full hover:bg-green-100"
                      >
                        ✅
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="btn-emoji text-xl w-9 h-9 bg-gray-50 rounded-full hover:bg-gray-100"
                      >
                        ❌
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* VIP 标签 */}
          <div className="mt-4">
            {user?.is_vip ? (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                {t('vipUser')}
              </div>
            ) : (
              <button
                onClick={() => setShowPaywall(true)}
                disabled={isProcessing}
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  isProcessing
                    ? 'bg-gray-300 text-gray-500'
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
                } transition-all`}
              >
                {isProcessing ? t('upgrading') : t('upgradeToVip')}
              </button>
            )}
          </div>
        </section>

        {/* 关联项目 */}
        <section>
          <h3 className="text-[10px] font-bold text-gray-400 mb-3 px-1 uppercase tracking-widest flex justify-between">
            <span>{t('relatedProjects')}</span>
            <span>{projects.length}</span>
          </h3>
          <div className="grid gap-3">
            {projects.length === 0 ? (
              <div className="bg-gray-100 rounded-xl p-8 text-center text-gray-400 text-xs italic">
                {t('noProjectRelated')}
              </div>
            ) : (
              projects.map((p) => {
                const isNegativeSystem = p.type == 1;
                const points = parseFloat(p.current_points);
                const isSupervisor = p.supervisor_id == user?.id;

                const colorClass = isNegativeSystem
                  ? points >= 0
                    ? 'text-red-500'
                    : 'text-green-500'
                  : points >= 0
                  ? 'text-green-500'
                  : 'text-red-500';

                return (
                  <div
                    key={p.project_id}
                    onClick={() => navigate(`/?pid=${p.project_id}`)}
                    className="bg-white p-4 rounded-xl shadow-sm border border-gray-50 flex justify-between items-center cursor-pointer active:bg-gray-50 transition-all"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-800 text-sm truncate">{p.title}</p>
                        <span
                          className={`text-[8px] px-1.5 py-0.5 rounded ${
                            isNegativeSystem ? 'bg-red-50 text-red-400' : 'bg-green-50 text-green-400'
                          } font-bold`}
                        >
                          {isNegativeSystem ? t('negativeMode') : t('positiveMode')}
                        </span>
                      </div>
                      <p className="text-[9px] text-gray-400 mt-1 uppercase tracking-tighter">
                        {t('identity')}：{isSupervisor ? t('supervisorIdentity') : t('performerIdentity')}
                      </p>
                    </div>
                    <span className={`text-sm font-black ${colorClass}`}>
                      {p.current_points}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* 退出登录 */}
        <button
          onClick={handleLogout}
          className="w-full bg-white text-red-500 border border-red-100 py-4 rounded-2xl font-bold shadow-sm active:bg-red-50 transition-all text-sm"
        >
          {t('logout')}
        </button>
      </main>

      {/* 头像选择弹窗 */}
      {showAvatarModal && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setShowAvatarModal(false)}
        >
          <div
            className="bg-white w-full max-w-xs rounded-3xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-gray-800">{t('selectNewAvatar')}</h3>
              <button
                onClick={() => setShowAvatarModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {AVATAR_LIST.map((emoji, idx) => (
                <button
                  key={idx}
                  onClick={() => updateAvatar(idx)}
                  className="avatar-option text-3xl p-2 rounded-xl hover:bg-indigo-50 transition-colors"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* VIP 升级弹窗 */}
      {showPaywall && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setShowPaywall(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-3xl p-6 shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-800">{t('upgradeToVip')}</h3>
              <button
                onClick={() => setShowPaywall(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                ✕
              </button>
            </div>
            <Paywall 
              onPurchaseSuccess={() => {
                // 购买成功后直接更新前端显示（后端通过 Webhook 异步更新）
                updateProfile({ is_vip: 1 });
              }}
              onClose={() => setShowPaywall(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
